import { computed, ref, ComputedRef, Ref } from 'vue';
import type { ContextMenuItems } from '@/common/utils';
import type {
  GridRawRowDataType,
  GridTableInstance,
  GridColumnInstance,
  GridColumnVisibilityOption,
} from '../../modernGrid.types';
import { getIsDefaultColumn } from '../../modernGrid.utils';

export const useColumnMenu = <RowDataType extends GridRawRowDataType>({
  table,
  columnVisibilityInfo,
  createSortingMenuItems,
  hideColumn,
  expandingColumn,
  setColumnSizingState,
}: {
  table: Ref<GridTableInstance<RowDataType> | undefined>;
  columnVisibilityInfo: ComputedRef<GridColumnVisibilityOption>;
  createSortingMenuItems: (column: GridColumnInstance<RowDataType>) => ContextMenuItems[];
  hideColumn: (column: GridColumnInstance<RowDataType>) => void;
  expandingColumn: Ref<string>;
  setColumnSizingState?: () => void;
}) => {
  const columnMenuRef = ref();
  const columnMenuItems = ref<ContextMenuItems[]>([]);

  const visibleColumnsCount = computed<number>(
    () =>
      table.value?.getVisibleLeafColumns().filter(({ id }) => !getIsDefaultColumn(id)).length || 0,
  );

  const adjustLastColumnSize = () => {
    if (!setColumnSizingState) return;
    setColumnSizingState();
  };

  const createDefaultColumnMenuItems = (
    column: GridColumnInstance<RowDataType>,
  ): ContextMenuItems[] => {
    const { hidden } = columnVisibilityInfo.value.columnMenu || {};

    return [
      !!hidden?.use &&
        !column.columns?.length && {
          text: hidden?.title || '',
          iconClass: hidden?.icon ? `${hidden?.icon} custom-icon` : '',
          disabled:
            !column.getCanHide() ||
            expandingColumn.value === column.id ||
            visibleColumnsCount.value === 1,
          click: () => {
            hideColumn(column);
            adjustLastColumnSize();
          },
        },
    ].filter(Boolean) as ContextMenuItems[];
  };

  const getColumnMenuItems = (column: GridColumnInstance<RowDataType>): ContextMenuItems[] => [
    ...createSortingMenuItems(column),
    ...createDefaultColumnMenuItems(column),
  ];

  const onClickHeaderCell = ({
    column,
    e,
  }: {
    column: GridColumnInstance<RowDataType>;
    e: MouseEvent;
  }): void => {
    columnMenuItems.value = getColumnMenuItems(column);
    columnMenuRef.value?.show(e);
  };

  return {
    columnMenuRef,
    columnMenuItems,
    onClickHeaderCell,
  };
};
